import {
  AsyncJobInterrupt,
  AsyncJobStatus,
  HitlInterrupt,
  SendMessageRes,
  ChatTurnResult,
  TurnDisplay,
} from "../type/chat";

export function parseHitlInterrupt(raw: unknown): HitlInterrupt | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  const data = raw as Record<string, unknown>;
  if (typeof data.thread_id !== "string" || typeof data.prompt !== "string") {
    return undefined;
  }
  const toolCalls = Array.isArray(data.tool_calls)
    ? data.tool_calls
        .filter((item): item is Record<string, unknown> => !!item && typeof item === "object")
        .map((item) => ({
          id: String(item.id ?? ""),
          name: String(item.name ?? ""),
          arguments: (item.arguments && typeof item.arguments === "object"
            ? item.arguments
            : {}) as Record<string, unknown>,
          summary: typeof item.summary === "string" ? item.summary : undefined,
        }))
    : [];
  return {
    thread_id: data.thread_id,
    prompt: data.prompt,
    tool_calls: toolCalls,
  };
}

export function parseAsyncJobInterrupt(raw: unknown): AsyncJobInterrupt | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  const data = raw as Record<string, unknown>;
  if (
    typeof data.thread_id !== "string" ||
    typeof data.job_id !== "string" ||
    typeof data.tool_name !== "string"
  ) {
    return undefined;
  }
  const status = data.status;
  const parsedStatus: AsyncJobStatus | undefined =
    status === "pending" || status === "completed" || status === "failed" ? status : undefined;
  return {
    thread_id: data.thread_id,
    job_id: data.job_id,
    tool_name: data.tool_name,
    status: parsedStatus,
    trigger_chat_no: typeof data.trigger_chat_no === "number" ? data.trigger_chat_no : undefined,
  };
}

export function parseTurnDisplay(raw: unknown): TurnDisplay | undefined {
  if (raw === "xcausal" || raw === "generative") {
    return raw;
  }
  return undefined;
}

export function parseRagTurnResult(data: SendMessageRes | Record<string, unknown>): ChatTurnResult {
  const payload = data as SendMessageRes;
  if (payload.chat_id && payload.chat_no !== undefined && payload.response) {
    const res = payload.response;
    return {
      chatId: payload.chat_id,
      chatNo: payload.chat_no,
      output: res.output || "",
      figure: res.figure,
      used_tools: res.used_tools,
      newModelId: res.next_model_id,
      queriedModelId: res.queried_model_id,
      artifactUrls: payload.artifact_urls ?? res.artifact_urls,
      interrupt: parseHitlInterrupt(payload.interrupt),
      asyncJob: parseAsyncJobInterrupt(payload.async_job),
      turnDisplay: parseTurnDisplay(res.turn_display),
    };
  }

  const fallback = data as Record<string, unknown>;
  const response = fallback.response as Record<string, unknown> | undefined;
  return {
    chatId: String(payload.chat_id || fallback.id || "unknown"),
    chatNo: Number(payload.chat_no || 1),
    output: String(fallback.output || ""),
    figure: fallback.figure,
    used_tools: fallback.used_tools as string[] | undefined,
    newModelId: fallback.new_model_id as string | undefined,
    queriedModelId: fallback.queried_model_id as string | undefined,
    artifactUrls: fallback.artifact_urls as ChatTurnResult["artifactUrls"],
    interrupt: parseHitlInterrupt(fallback.interrupt),
    asyncJob: parseAsyncJobInterrupt(fallback.async_job),
    turnDisplay: parseTurnDisplay(response?.turn_display ?? fallback.turn_display),
  };
}
